import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";
import MoneyFlow from "../components/MoneyFlow";

/**
 * Scene · Revenue Projection · 50,000 videos.
 *
 *   • 50,000 videos × £45  = £2,250,000  (core video revenue)
 *   • 50,000 × 50% × £15   = £375,000    (Social Go / Social Pro bolt-on)
 *   ─────────────────────────────────────────────────────────────────
 *   Total                  = £2,625,000
 *
 * Sits after saving-240k so the room flips from cost-savings to a clean
 * revenue line.
 */

const GOLD = "#e9a820";
const GREEN = "#96C11F";
const AQUA = "#00deb6";

export default function SceneRevenueProjection() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-revenue-projection">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at center, rgba(233,168,32,0.16) 0%, transparent 60%)",
            "radial-gradient(ellipse at center, rgba(150,193,31,0.22) 0%, transparent 60%)",
            "radial-gradient(ellipse at center, rgba(233,168,32,0.16) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      <MoneyFlow density={36} />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-3xl sm:text-5xl lg:text-6xl text-white text-center mb-14"
          data-testid="rev-proj-title"
        >
          Now flip it to{" "}
          <span
            style={{
              background: `linear-gradient(135deg, ${GOLD} 0%, #d4940a 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            revenue.
          </span>
        </motion.h2>

        {/* Two stacked rows · core + bolt-on */}
        <div className="space-y-5 max-w-[1100px] mx-auto">
          {/* CORE VIDEO REVENUE */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.0, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl glass p-7 grid grid-cols-12 items-center gap-4"
            style={{ boxShadow: "0 0 50px rgba(150,193,31,0.14)" }}
            data-testid="rev-row-core"
          >
            <div className="col-span-12 sm:col-span-5 flex items-center gap-3">
              <span
                className="text-[10px] tracking-[0.32em] uppercase"
                style={{ color: GREEN, fontWeight: 700 }}
              >
                Core video
              </span>
              <span
                className="hidden sm:inline-block w-px h-3"
                style={{ background: "rgba(255,255,255,0.16)" }}
              />
              <span className="text-sm sm:text-base text-white/85 tnum">
                50,000 <span className="text-white/40">×</span>{" "}
                <span style={{ color: GREEN }}>£45</span>
              </span>
            </div>
            <div className="col-span-12 sm:col-span-7 sm:text-right">
              <span
                className="kn-display tnum text-[44px] sm:text-[56px] lg:text-[64px] leading-none"
                style={{
                  color: GREEN,
                  fontWeight: 800,
                  textShadow: "0 0 30px rgba(150,193,31,0.30)",
                }}
              >
                <CountUp
                  from={0}
                  to={2250000}
                  prefix="£"
                  duration={2.0}
                  startDelay={0.9}
                />
              </span>
            </div>
          </motion.div>

          {/* BOLT-ON · Social Go / Social Pro */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.0, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl glass p-7 grid grid-cols-12 items-center gap-4"
            style={{ boxShadow: "0 0 50px rgba(0,222,182,0.14)" }}
            data-testid="rev-row-boltn"
          >
            <div className="col-span-12 sm:col-span-5 flex items-center gap-3 flex-wrap">
              <span
                className="text-[10px] tracking-[0.32em] uppercase"
                style={{ color: AQUA, fontWeight: 700 }}
              >
                Bolt-on · Go / Pro
              </span>
              <span
                className="hidden sm:inline-block w-px h-3"
                style={{ background: "rgba(255,255,255,0.16)" }}
              />
              <span className="text-sm sm:text-base text-white/85 tnum">
                50,000 <span className="text-white/40">×</span> 50%{" "}
                <span className="text-white/40">×</span>{" "}
                <span style={{ color: AQUA }}>£15</span>
              </span>
            </div>
            <div className="col-span-12 sm:col-span-7 sm:text-right">
              <span
                className="kn-display tnum text-[44px] sm:text-[56px] lg:text-[64px] leading-none"
                style={{
                  color: AQUA,
                  fontWeight: 800,
                  textShadow: "0 0 30px rgba(0,222,182,0.30)",
                }}
              >
                <CountUp
                  from={0}
                  to={375000}
                  prefix="£"
                  duration={2.0}
                  startDelay={1.9}
                />
              </span>
            </div>
          </motion.div>

          {/* Hairline */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1.0, delay: 2.7 }}
            className="h-px origin-center"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`,
            }}
          />

          {/* TOTAL */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 3.0, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl p-8 grid grid-cols-12 items-center gap-4 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(233,168,32,0.10) 0%, rgba(212,148,10,0.06) 100%)",
              border: `1px solid ${GOLD}55`,
              boxShadow: `0 30px 80px -20px rgba(0,0,0,0.55), 0 0 60px ${GOLD}22`,
            }}
            data-testid="rev-row-total"
          >
            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ opacity: [0.5, 0.95, 0.5] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse at 80% 50%, rgba(233,168,32,0.25) 0%, transparent 60%)",
              }}
            />
            <div className="col-span-12 sm:col-span-5 relative">
              <span
                className="text-[11px] tracking-[0.32em] uppercase"
                style={{ color: GOLD, fontWeight: 700 }}
              >
                Total · annual revenue
              </span>
            </div>
            <div className="col-span-12 sm:col-span-7 sm:text-right relative">
              <span
                className="kn-display tnum leading-[0.92] whitespace-nowrap"
                style={{
                  fontSize: "clamp(56px, 7vw, 104px)",
                  background: `linear-gradient(135deg, #d4940a 0%, ${GOLD} 50%, #d4940a 100%)`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  fontWeight: 900,
                  textShadow: `0 0 60px ${GOLD}55`,
                }}
              >
                <CountUp
                  from={0}
                  to={2625000}
                  prefix="£"
                  duration={2.6}
                  startDelay={3.3}
                />
              </span>
            </div>
          </motion.div>
        </div>

        {/* Sub-anchor */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.0, delay: 5.6 }}
          className="mt-14 text-center text-sm tracking-[0.18em] uppercase"
          style={{ color: "rgba(255,255,255,0.65)", fontWeight: 600 }}
        >
          Same shoot · same property · new revenue line
        </motion.div>
      </div>
    </SceneShell>
  );
}
